/**
 * The unit this person reads and types loads in, `kg` or `lb` (`D266`).
 *
 * ── THE ACCOUNT'S, CACHED ON THE DEVICE ────────────────────────────────────
 * The truth is `account.weight_unit` on the server (`GET /auth/me`), so a
 * person with two phones sees one unit. This store is that value held
 * SYNCHRONOUSLY, for two reasons: the formatters in `lib/weight.ts` are
 * called outside React (a share card, a message param) and cannot wait for
 * a query; and the first render after a cold start must already be in the
 * person's unit, so the last value seen is kept in `localStorage` and read
 * the moment this module loads — the same trade `stores/prefs.ts` makes for
 * the theme.
 *
 * `WeightUnitBridge` (`features/profile/weight-unit-bridge.tsx`) moves the
 * server's value in whenever `me` is read; the settings row writes here
 * first and then `PUT /auth/me/weight-unit` through the outbox. `App` keys
 * the router on the unit, as it does on the locale, so a change re-renders
 * every screen at once.
 *
 * ── DISPLAY ONLY ───────────────────────────────────────────────────────────
 * Nothing stored, sent or computed is in this unit. See `D266`.
 */
import type { WeightUnit } from "@repo/contract";
import { useStore } from "zustand";
import { createStore } from "zustand/vanilla";

export const WEIGHT_UNIT_STORAGE_KEY = "mypocketgym-weight-unit";

interface WeightUnitState {
  readonly unit: WeightUnit;
}

function readStored(): WeightUnit {
  try {
    const stored = globalThis.localStorage?.getItem(WEIGHT_UNIT_STORAGE_KEY);
    return stored === "lb" ? "lb" : "kg";
  } catch {
    return "kg";
  }
}

const store = createStore<WeightUnitState>()(() => ({ unit: readStored() }));

/** The unit now, synchronously — for a formatter outside React. */
export function weightUnit(): WeightUnit {
  return store.getState().unit;
}

/** The unit, re-rendering the caller when it changes. */
export function useWeightUnit(): WeightUnit {
  return useStore(store, (state) => state.unit);
}

/** Sets the unit on this device and remembers it for the next cold start. */
export function setWeightUnit(unit: WeightUnit): void {
  if (store.getState().unit !== unit) {
    store.setState({ unit });
  }
  try {
    globalThis.localStorage?.setItem(WEIGHT_UNIT_STORAGE_KEY, unit);
  } catch {
    // Not remembered: the next launch starts in kilograms until `me` arrives.
  }
}

/**
 * Back to kilograms and nothing remembered — the account is leaving this
 * device (`data/provider.tsx`'s `ACCOUNT_DATA`). The next person's unit
 * arrives with their own `me`.
 */
export function forgetWeightUnit(): void {
  store.setState({ unit: "kg" });
  try {
    globalThis.localStorage?.removeItem(WEIGHT_UNIT_STORAGE_KEY);
  } catch {
    // Nothing to remove.
  }
}
